import React from "react"
import { Dialog, DialogTitle, DialogContent, Box, Button } from "@material-ui/core"
import { NavLink, useHistory, useParams } from "react-router-dom"
import moment from "moment"
import { DialogTitleCloser } from "../Creators/DialogTitleCloser"
import { InlineTitleCloser } from "../Creators"
import { Icon, InlineTitle } from ".."
import { TrainingCurriculum } from "./TrainingCurriculum"
import { ColumnBox, RowBox } from "../Boxes"
import { useQueryUserTrainings } from "../../data"
import { useAuth } from "../../services"
import { toId } from "../../utils"
import { IconMessage } from "../Lists"
import { Blockquote } from "../Blockquotes"
import TimeAgo from "../TimeAgo"
import { FormatDate } from "../Format"

const TrainingViewer = ({ isInline }) => {
  const history = useHistory()
  const { id } = useParams()
  const {
    principal: { userID: user },
  } = useAuth()
  const { data } = useQueryUserTrainings({ variables: { user } })

  const handleClose = () => {
    history.push("/knowledge")
  }

  if (!user) {
    handleClose()
    return null
  }

  const thisTraining = data?.userTrainings.find((training) => toId(training) === id)
  if (!thisTraining) {
    handleClose()
    return null
  }

  const complete = Boolean(thisTraining.status.completedAt)
  const overdue = thisTraining.dueAt ? moment(thisTraining.dueAt).isBefore(moment()) : false

  // get next if present
  let nextUserTraining = null
  if (complete) {
    nextUserTraining = data?.userTrainings.find((userTraining) => !userTraining.status.completedAt)
  }

  const display = (
    <>
      <Box px={isInline ? 2 : 0} py={isInline ? 2 : 0} data-cy="TrainingViewer-display">
        {!complete && (
          <>
            {!overdue && Boolean(thisTraining.dueAt) && (
              <Box mb={3}>
                <Blockquote
                  icon={<Icon name="due" />}
                  title={
                    <>
                      Training due <TimeAgo date={thisTraining.dueAt} />
                    </>
                  }
                  body={
                    <>
                      Please complete {thisTraining.name} by{" "}
                      <strong>
                        <FormatDate value={thisTraining.dueAt} />
                      </strong>
                      . Contact your manager if you are having any trouble.
                    </>
                  }
                />
              </Box>
            )}
            {overdue && (
              <Box mb={3}>
                <Blockquote
                  icon={<Icon name="due" />}
                  title="Training overdue"
                  body="Your training is now overdue, please complete it as soon as possible. Please contact your manager if you are having trouble completing this training."
                  color="warning"
                />
              </Box>
            )}

            <TrainingCurriculum key={toId(thisTraining)} training={thisTraining} />
          </>
        )}
        {complete && (
          <Box mb={5}>
            <IconMessage
              name="training-complete"
              maxWidth={400}
              text={
                <>
                  Congratulations!
                  <br />
                  You've completed the '{thisTraining.name}' course!
                  <br />
                  <br />
                  You can always access your training history from the Settings area of the app.
                </>
              }
            />

            {nextUserTraining && (
              <ColumnBox alignItems="center" mt={3}>
                <Button
                  component={NavLink}
                  to={`/knowledge/training/${toId(nextUserTraining)}`}
                  variant="contained"
                  color="primary"
                >
                  Continue to {nextUserTraining.name}
                </Button>
              </ColumnBox>
            )}
          </Box>
        )}
      </Box>
    </>
  )

  if (isInline) {
    return (
      <>
        <InlineTitle>
          Your Training
          <InlineTitleCloser onClose={handleClose} />
        </InlineTitle>
        {display}
      </>
    )
  }
  return (
    <Dialog
      open
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      fullWidth
      maxWidth="md"
      data-cy="TrainingViewer-dialog"
    >
      <DialogTitle id="form-dialog-title">
        <RowBox>
          <Icon name="training" />
          <Box ml={1}>{thisTraining.name}</Box>
        </RowBox>
        <DialogTitleCloser onClose={handleClose} />
      </DialogTitle>
      <DialogContent>{display}</DialogContent>
    </Dialog>
  )
}

export { TrainingViewer }
